<template>
  <div>
    <v-dialog
      v-model="isOpen"
      width="700"
      persistent
      scrollable
      :fullscreen="$vuetify.breakpoint.smAndDown"
      content-class="rounded-lg"
    >
      <v-card class="h-full d-flex flex-column" style="max-height: 90dvh">
        <v-card-title class="pb-0 pt-3 px-0 flex-column">
          <div class="d-flex w-full align-center px-6 gap-2">
            <span class="text-16">
              Editar {{ ticketBlocks.length }} lote{{
                ticketBlocks.length > 1 ? "s" : ""
              }}
            </span>
            <v-spacer />
          </div>
          <div class="w-full">
            <v-stepper
              v-model="step"
              class="elevation-0 text-14 font-weight-regular"
            >
              <v-stepper-header class="elevation-0 mb-0" style="height: 38px">
                <template v-for="(stp, i) in steps">
                  <v-divider v-if="i" :key="`divider-` + i" />
                  <v-stepper-step
                    :key="`step-` + i"
                    :complete="step > i + 1"
                    :step="i + 1"
                    class="py-0"
                  >
                    {{ stp }}
                  </v-stepper-step>
                </template>
              </v-stepper-header>
            </v-stepper>
          </div>
        </v-card-title>

        <v-card-text class="pa-0 ma-0">
          <v-stepper v-model="step" class="elevation-0">
            <v-stepper-items>
              <v-stepper-content step="1" class="pt-0">
                <v-form ref="form" v-model="valid">
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <v-card class="pa-2" outlined>
                          <v-switch
                            v-model="editActive.quantity"
                            label="Editar quantidade"
                            hide-details="auto"
                            class="mb-2 mt-0"
                          />
                          <v-text-field
                            v-model="editData.quantity"
                            label="Quantidade"
                            type="number"
                            hide-details="auto"
                            :disabled="!editActive.quantity"
                            outlined
                            dense
                            :rules="[
                              (v) =>
                                !editActive.quantity ||
                                v >= 0 ||
                                'Quantidade deve ser maior que 0',
                            ]"
                          />
                        </v-card>
                      </v-col>

                      <v-col cols="12">
                        <v-card class="pa-2 d-flex gap-4" outlined>
                          <div class="d-flex flex-column flex-grow-1">
                            <v-switch
                              v-model="editActive.startDate"
                              label="Editar data de início"
                              hide-details="auto"
                              class="mb-2 mt-0"
                            />
                            <v-text-field
                              v-model="editData.startDate"
                              label="Data de início"
                              type="datetime-local"
                              hide-details="auto"
                              outlined
                              dense
                              :disabled="!editActive.startDate"
                              :rules="[
                                (v) =>
                                  !editActive.startDate ||
                                  !!v ||
                                  'Data de início é obrigatória',
                              ]"
                            />
                          </div>
                          <div class="d-flex flex-column flex-grow-1">
                            <v-switch
                              v-model="editActive.endDate"
                              label="Editar data de término"
                              hide-details="auto"
                              class="mb-2 mt-0"
                            />
                            <v-text-field
                              v-model="editData.endDate"
                              label="Data de término"
                              type="datetime-local"
                              hide-details="auto"
                              outlined
                              dense
                              :disabled="!editActive.endDate"
                              :rules="[
                                (v) =>
                                  !editActive.endDate ||
                                  !!v ||
                                  'Data de término é obrigatória',
                              ]"
                            />
                          </div>
                        </v-card>
                      </v-col>

                      <v-col cols="6">
                        <v-card class="pa-2" outlined>
                          <v-switch
                            class="mt-0"
                            v-model="editActive.onlineSale"
                            label="Editar venda online"
                            hide-details="auto"
                          />
                          <v-switch
                            class="mt-0"
                            v-model="editData.onlineSale"
                            label="Venda online"
                            hide-details="auto"
                            :disabled="!editActive.onlineSale"
                            :messages="[
                              editData.onlineSale
                                ? 'Venda online permitida'
                                : 'Venda online não permitida',
                            ]"
                          />
                        </v-card>
                      </v-col>

                      <v-col cols="6">
                        <v-card class="pa-2" outlined>
                          <v-switch
                            class="mt-0"
                            v-model="editActive.allowTransfer"
                            label="Editar permissão de transferência"
                            hide-details="auto"
                          />
                          <v-switch
                            class="mt-0"
                            v-model="editData.allowTransfer"
                            label="Permitir transferência"
                            hide-details="auto"
                            :disabled="!editActive.allowTransfer"
                            :messages="[
                              editData.allowTransfer
                                ? 'Transferência permitida'
                                : 'Transferência não permitida',
                            ]"
                          />
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-form>
              </v-stepper-content>
              <v-stepper-content step="2" class="pt-0">
                <v-card class="pa-2" outlined>
                  <v-switch
                    v-model="editActive.sellers"
                    label="Editar vendedores"
                    hide-details="auto"
                    class="mb-2 mt-0"
                  />
                  <ticket-block-sellers
                    v-model="sellers"
                    :members="members"
                    :posSessions="posSessions"
                    :class="{
                      'opacity-50 pointer-events-none': !editActive.sellers,
                    }"
                  />
                </v-card>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>

        <v-card-actions class="d-flex align-end gap-1 flex-wrap">
          <template v-if="step === 1">
            <v-spacer />
            <v-btn text :disabled="loading" @click="close">Cancelar</v-btn>
            <v-btn
              color="primary"
              :disabled="!valid || loading"
              @click="step++"
            >
              Continuar
            </v-btn>
          </template>
          <template v-if="step === 2">
            <v-spacer />
            <v-btn text :disabled="loading" @click="step--">Voltar</v-btn>
            <v-btn
              color="primary"
              :disabled="loading || !hasChanges"
              :loading="loading"
              @click="save"
            >
              Salvar
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TICKET from "@/services/admin/ticket";
import TicketBlockSellers from "./modules/TicketBlockSellers.vue";
import moment from "moment";

export default {
  components: {
    TicketBlockSellers,
  },
  data: () => ({
    isOpen: false,
    loading: false,
    valid: true,
    step: 1,
    ticketBlocks: [],
    editActive: {
      quantity: false,
      startDate: false,
      endDate: false,
      onlineSale: false,
      allowTransfer: false,
      sellers: false,
    },
    editData: {
      price: null,
      startDate: moment().format("YYYY-MM-DDTHH:mm"),
      endDate: null,
      onlineSale: false,
      allowTransfer: false,
    },
    sellers: [],
    steps: ["Informações", "Vendedores"],
  }),
  computed: {
    hasChanges() {
      return (
        (this.editActive.quantity && this.editData.quantity >= 0) ||
        (this.editActive.startDate && this.editData.startDate) ||
        (this.editActive.endDate && this.editData.endDate) ||
        this.editActive.onlineSale ||
        this.editActive.allowTransfer ||
        (this.editActive.sellers && this.sellers.length > 0)
      );
    },
  },
  methods: {
    open(ticketBlocks) {
      this.isOpen = true;
      this.ticketBlocks = ticketBlocks;
      this.valid = true;
      this.step = 1;
      this.editActive = {
        quantity: false,
        startDate: false,
        endDate: false,
        onlineSale: false,
        allowTransfer: false,
        sellers: false,
      };
      this.editData = {
        quantity: null,
        startDate: moment().format("YYYY-MM-DDTHH:mm"),
        endDate: moment(this.party.endDate).format("YYYY-MM-DDTHH:mm"),
        onlineSale: false,
        allowTransfer: false,
      };
    },
    close() {
      this.isOpen = false;
      this.loading = false;
      this.step = 1;
      this.ticketBlocks = [];
      this.sellers = [];
    },
    async save() {
      try {
        this.loading = true;
        const orgId = this.party.organizationId;
        const partyId = this.party.id;

        const sellersGrouped = this.sellers.reduce(
          (acc, s) => {
            if (!acc[s.type]) acc[s.type] = [];
            acc[s.type].push(s.id);
            return acc;
          },
          { user: [], pos: [] }
        );

        const updates = this.ticketBlocks.map(async (ticketBlock) => {
          const data = {
            ...(this.editActive.quantity && {
              quantity: this.editData.quantity,
            }),
            ...(this.editActive.startDate && {
              startDate: moment(this.editData.startDate).toISOString(),
            }),
            ...(this.editActive.endDate && {
              endDate: moment(this.editData.endDate).toISOString(),
            }),
            ...(this.editActive.onlineSale && {
              onlineSale: this.editData.onlineSale,
            }),
            ...(this.editActive.allowTransfer && {
              allowTransfer: this.editData.allowTransfer,
            }),
            ...(this.editActive.sellers && {
              sellers: sellersGrouped.user,
              posSellers: sellersGrouped.pos,
            }),
          };

          await TICKET.updateTicketBlock(
            orgId,
            partyId,
            ticketBlock.id,
            data
          ).catch((error) => {
            console.error(error);
          });
        });

        await Promise.all(updates);
        this.$emit("success");
        this.close();
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.$root.$on("ticket-block-multiple-edit-modal", this.open);
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
    posSessions: {
      type: Array,
      default: () => [],
    },
    members: {
      type: Array,
      default: () => [],
    },
  },
};
</script>

<style>
.opacity-50 {
  opacity: 0.5;
}
.pointer-events-none {
  pointer-events: none;
}
</style>
